import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import campusService from '../../services/campusService';
import DialogModal from '../../components/DialogModal';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';

const emptyForm = { name: '', address: '', phone: '', is_main_campus: false };

function CampusesPage() {
  const { user } = useAuth();
  const isCampusScoped = !!user?.campus_id;
  const [loading, setLoading] = useState(true);
  const [campuses, setCampuses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await campusService.listCampuses();
      setCampuses(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load campuses');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (campus) => {
    setEditingId(campus.id);
    setFormData({
      name: campus.name,
      address: campus.address || '',
      phone: campus.phone || '',
      is_main_campus: !!campus.is_main_campus,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await campusService.updateCampus(editingId, formData);
        toast.success('Campus updated');
      } else {
        await campusService.createCampus(formData);
        toast.success('Campus created');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save campus');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await campusService.deleteCampus(deleteTarget);
      toast.success('Campus deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete campus');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading campuses...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Campuses</h1>
            <p>
              {isCampusScoped
                ? 'Your account is restricted to the campus below.'
                : 'Manage campus branches — scoping students, staff, classes, admin accounts, and fee structures.'}
            </p>
          </div>
          {!isCampusScoped && (
            <button className="btn btn-primary" onClick={openCreateModal}>
              <Plus size={20} />
              Add Campus
            </button>
          )}
        </div>

        <div className="table-container">
          {campuses.length === 0 ? (
            <div className="empty-state">
              <p>No campuses found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <th>Main Campus</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campuses.map((campus) => (
                  <tr key={campus.id}>
                    <td>{campus.name}</td>
                    <td>{campus.address || '-'}</td>
                    <td>{campus.phone || '-'}</td>
                    <td>{campus.is_main_campus ? <span className="badge active">Main</span> : '-'}</td>
                    <td className="action-buttons">
                      <button className="btn-icon" title="Edit" onClick={() => openEditModal(campus)}>
                        <PencilSimple size={18} />
                      </button>
                      {!isCampusScoped && (
                        <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteTarget(campus.id)}>
                          <Trash size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingId ? 'Edit Campus' : 'Add Campus'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="text" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_main_campus}
                    onChange={(e) => setFormData({ ...formData, is_main_campus: e.target.checked })}
                    style={{ marginRight: 8 }}
                  />
                  Main campus
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Campus"
        message="This will permanently delete this campus. Students, staff, and classes referencing it will simply show no campus. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default CampusesPage;
